// /src/pages/private/members/MembersOnboardingSteps.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Hls from 'hls.js';
import PrivateLexicalPage from '../../../components/PrivateLexicalPage';
import MembersOnboardingSectionMedia from './MembersOnboardingSectionMedia';
import { resolveMediasImageGallery } from '../../../utils/onboardingElementMedia';
import OrgStructureSelector from './shared/OrgStructureSelector';

const API_USER_ELEMENT_SECTIONS = '/api/user/onboarding/element-sections';
const API_ONBOARDING_STATUS = '/api/user/onboarding/status';
const API_VALIDATE_STEP = '/api/user/onboarding/validate-step';
function clampDwellInt(n, min, max) {
  const x = parseInt(n, 10);
  if (Number.isNaN(x)) return min;
  return Math.min(max, Math.max(min, x));
}

/** Total minimum dwell seconds from card `payload` (admin). 0 if disabled or absent. */
function getMinimumDwellTotalSeconds(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return 0;
  if (!payload.minimumDwellEnabled) return 0;
  const m = clampDwellInt(payload.minimumDwellMinutes, 0, 599);
  const s = clampDwellInt(payload.minimumDwellSeconds, 0, 59);
  return m * 60 + s;
}

function formatMmSs(totalSeconds) {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '0:00';
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function parseMmSsToSeconds(str) {
  if (typeof str !== 'string') return null;
  const parts = str.trim().split(':');
  if (parts.length !== 2) return null;
  const m = parseInt(parts[0], 10);
  const s = parseInt(parts[1], 10);
  if (Number.isNaN(m) || Number.isNaN(s)) return null;
  return m * 60 + s;
}

/** Display like `00:06` for nav when the badge is a mm:ss countdown */
function formatNavClockDisplay(label) {
  if (!label) return null;
  if (label === 'Scroll' || label === 'Unread') return label;
  const sec = parseMmSsToSeconds(label);
  if (sec == null) return label;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(Math.min(m, 99)).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function navTimerRemainingFraction(navBadge, payload) {
  const total = getMinimumDwellTotalSeconds(payload);
  if (total <= 0) return null;
  if (navBadge === 'Scroll' || navBadge === 'Unread' || !navBadge) return null;
  const rem = parseMmSsToSeconds(navBadge);
  if (rem == null || rem < 0) return null;
  return Math.min(1, Math.max(0, rem / total));
}

function lexicalProgressToNavBadge(payload) {
  if (!payload) return null;
  const min = payload.minimumTotalSeconds || 0;
  const rem = payload.minimumRemainingSeconds;
  const scroll = payload.scrollReachedEnd;
  if (min > 0 && rem != null && rem > 0) return formatMmSs(rem);
  if (!scroll) return 'Unread';
  return null;
}

function MembersOnboardingNavClockIcon() {
  return (
    <span className="members-onboarding__steps-nav-clock" aria-hidden="true">
      <svg
        className="members-onboarding__steps-nav-clockSvg"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
        <path
          d="M12 8v4l3 2"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

/* r=18 : le disque plein touche les bords du viewBox 36×36 (plus de bande sous la bordure du bouton). */
const STEP_DISC_R = 18;
const STEP_DISC_C = 2 * Math.PI * STEP_DISC_R;

function MembersOnboardingStepDisc({ stepIndex, isActive, locked, remainingFraction }) {
  const label = String(stepIndex + 1).padStart(2, '0');
  const cx = 18;
  const cy = 18;
  const showRing =
    isActive &&
    typeof remainingFraction === 'number' &&
    remainingFraction > 0.001;
  const arcLen = showRing ? STEP_DISC_C * Math.min(1, remainingFraction) : 0;

  return (
    <div className="members-onboarding__steps-nav-discWrap">
      <svg
        className="members-onboarding__steps-nav-discSvg"
        viewBox="0 0 36 36"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        {/* Opaque disc behind everything: hides the vertical connector line that runs through disc centers */}
        <circle
          className="members-onboarding__steps-nav-discBg"
          cx={cx}
          cy={cy}
          r={STEP_DISC_R}
        />
        <circle
          className="members-onboarding__steps-nav-discTrack"
          cx={cx}
          cy={cy}
          r={STEP_DISC_R}
          fill="none"
          strokeWidth="2"
        />
        {showRing ? (
          <circle
            className="members-onboarding__steps-nav-discProgress"
            cx={cx}
            cy={cy}
            r={STEP_DISC_R}
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={`${arcLen} ${STEP_DISC_C}`}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        ) : null}
      </svg>
      <span
        className={[
          'members-onboarding__steps-nav-discLabel',
          isActive ? 'members-onboarding__steps-nav-discLabel--active' : '',
          locked ? 'members-onboarding__steps-nav-discLabel--locked' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {label}
      </span>
    </div>
  );
}

function MembersOnboardingStepsMainBgVideo({ src }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return undefined;

    const tryPlay = () => {
      video.play().catch(() => {});
    };

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      video.addEventListener('loadeddata', tryPlay);
      return () => {
        video.removeEventListener('loadeddata', tryPlay);
        video.removeAttribute('src');
        video.load();
      };
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        startLevel: 0,
        autoStartLoad: true,
        capLevelToPlayerSize: false,
      });
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src);
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data?.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          hls.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        } else {
          hls.destroy();
          hlsRef.current = null;
        }
      });
      hls.attachMedia(video);
      hlsRef.current = hls;
      video.addEventListener('loadeddata', tryPlay);

      return () => {
        video.removeEventListener('loadeddata', tryPlay);
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
        video.removeAttribute('src');
        video.load();
      };
    }

    return undefined;
  }, [src]);

  if (!src) return null;

  return (
    <div className="members-onboarding__steps-main-videoBg" aria-hidden>
      <video
        ref={videoRef}
        className="members-onboarding__steps-main-video"
        muted
        playsInline
        loop
        autoPlay
      />
    </div>
  );
}

function MembersOnboardingStepPanel({
  section,
  validationActive,
  sectionCompleted,
  masterTags,
  onStepComplete,
  onNavBadge,
  onStructureBackgroundVideoChange,
}) {
  const handleStepValidated = useCallback(() => {
    onStepComplete(section.id);
  }, [section.id, onStepComplete]);

  const reportLexicalNav = useCallback(
    (payload) => {
      onNavBadge?.(section.id, lexicalProgressToNavBadge(payload));
    },
    [section.id, onNavBadge],
  );

  const reportImageSecondsNav = useCallback(
    (sec) => {
      if (sec == null || sec <= 0) onNavBadge?.(section.id, null);
      else onNavBadge?.(section.id, formatMmSs(sec));
    },
    [section.id, onNavBadge],
  );

  const type = section.type;
  const payload =
    section.payload && typeof section.payload === 'object' && !Array.isArray(section.payload)
      ? section.payload
      : {};
  const media =
    payload.media && typeof payload.media === 'object' && !Array.isArray(payload.media)
      ? payload.media
      : null;
  const imageGallery = resolveMediasImageGallery(payload, media);

  const minimumDwellTotalSeconds = getMinimumDwellTotalSeconds(payload);

  switch (type) {
    case 'textFromSource': {
      const name = String(section.sourceLexicalDocumentName || '').trim();
      if (!name) {
        return (
          <p className="members-onboarding-steps__empty">
            No document is linked to this step yet.
          </p>
        );
      }
      return (
        <PrivateLexicalPage
          key={`${section.id}-${name}`}
          documentName={name}
          wrapperClass="members-onboarding-steps__doc"
          containerClass="onboarding-private-container"
          hidePublicToc
          hideReadProgressMeta
          onReadProgressReport={reportLexicalNav}
          onFullScrollComplete={handleStepValidated}
          scrollCompletionActive={validationActive}
          minimumReadSeconds={minimumDwellTotalSeconds}
        />
      );
    }

    case 'createText': {
      const documentName = `onboarding-${section.id}`;
      return (
        <PrivateLexicalPage
          key={documentName}
          documentName={documentName}
          wrapperClass="members-onboarding-steps__doc"
          containerClass="onboarding-private-container"
          hideReadProgressMeta
          onReadProgressReport={reportLexicalNav}
          onFullScrollComplete={handleStepValidated}
          scrollCompletionActive={validationActive}
          minimumReadSeconds={minimumDwellTotalSeconds}
        />
      );
    }

    case 'structure': {
      // Les tags sortent désormais du pool global `masterTags` servi au
      // niveau racine de la réponse element-sections (et non plus du
      // payload de la card). Le step rend les chips à partir d'entités
      // { id, label } pour pouvoir envoyer des IDs stables au backend.
      return (
        <OrgStructureSelector
          key={section.id}
          tagsHelpIdPrefix={section.id}
          masterTags={Array.isArray(masterTags) ? masterTags : []}
          isValidationActive={validationActive}
          onStructureBackgroundVideoChange={onStructureBackgroundVideoChange}
          onConfirm={async (structureChoice) => {
            await onStepComplete(section.id, { structureChoice });
          }}
          validateHelpText="Review your division, branch, section, and membership title, then confirm to proceed. You can click a breadcrumb or card to change a choice."
          confirmLabel={validationActive ? 'Confirm' : 'update my choices'}
        />
      );
    }

    case 'medias':
      return (
        <MembersOnboardingSectionMedia
          key={section.id}
          media={media}
          imageGallery={imageGallery}
          minimumDwellTotalSeconds={minimumDwellTotalSeconds}
          isValidationActive={validationActive}
          sectionCompleted={sectionCompleted}
          onValidationComplete={handleStepValidated}
          suppressImageTimer
          onImageCountdownTick={reportImageSecondsNav}
        />
      );

    default:
      return (
        <p className="members-onboarding-steps__empty">This step type cannot be displayed here.</p>
      );
  }
}

export default function MembersOnboardingSteps() {
  const navigate = useNavigate();

  const [sections, setSections] = useState([]);
  // Pool global de tags applicatifs servi par l'endpoint element-sections.
  // Format renvoyé par le backend : Array<{ id: number, label: string }>.
  // L'ordre du tableau respecte sortOrder ASC, label ASC côté backend — on
  // garde l'ordre tel quel pour rendre les chips.
  const [masterTags, setMasterTags] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [completedById, setCompletedById] = useState({});
  const [navBadgeById, setNavBadgeById] = useState({});
  const [stepPersistError, setStepPersistError] = useState(null);
  const [structureBgHlsUrl, setStructureBgHlsUrl] = useState(null);

  const handleStructureBackgroundVideoChange = useCallback((url) => {
    setStructureBgHlsUrl(url ? String(url).trim() || null : null);
  }, []);

  const setNavBadge = useCallback((sectionId, label) => {
    setNavBadgeById((prev) => {
      const cleared = label == null || label === '';
      if (cleared) {
        if (!(sectionId in prev)) return prev;
        const next = { ...prev };
        delete next[sectionId];
        return next;
      }
      if (prev[sectionId] === label) return prev;
      return { ...prev, [sectionId]: label };
    });
  }, []);

  useEffect(() => {
    setNavBadgeById({});
  }, [selectedId]);

  useEffect(() => {
    if (selected?.type !== 'structure') {
      setStructureBgHlsUrl(null);
    }
  }, [selected?.type, selected?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setListLoading(true);
        setListError(null);
        const { data } = await axios.get(API_USER_ELEMENT_SECTIONS, {
          withCredentials: true,
        });
        const rows = Array.isArray(data?.sections) ? data.sections : [];
        const tagRows = Array.isArray(data?.masterTags) ? data.masterTags : [];
        if (!cancelled) {
          setSections(rows);
          // Filtrage défensif : on garde uniquement les entrées avec un id
          // entier > 0 et un label string non vide. Le backend respecte
          // déjà ce contrat mais ça nous met à l'abri d'une réponse foireuse.
          setMasterTags(
            tagRows
              .map((t) => ({
                id: Number(t?.id),
                label: typeof t?.label === 'string' ? t.label.trim() : '',
              }))
              .filter((t) => Number.isInteger(t.id) && t.id > 0 && t.label.length > 0),
          );
        }
      } catch (e) {
        if (!cancelled) {
          setListError(e?.response?.data?.message || e?.message || 'Unable to load onboarding steps.');
          setSections([]);
          setMasterTags([]);
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!sections.length) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(API_ONBOARDING_STATUS, { withCredentials: true });
        if (cancelled) return;
        const ids = Array.isArray(data?.validatedCardIds) ? data.validatedCardIds : [];
        setCompletedById((prev) => {
          const next = { ...prev };
          for (const id of ids) {
            if (sections.some((s) => s.id === id)) next[id] = true;
          }
          return next;
        });
      } catch {
        // Server progress optional; user can still complete steps locally
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sections]);

  useEffect(() => {
    if (!sections.length) return;
    setSelectedId((prev) => {
      if (prev && sections.some((s) => s.id === prev)) return prev;
      return sections[0].id;
    });
  }, [sections]);

  const firstIncompleteIndex = useMemo(() => {
    for (let i = 0; i < sections.length; i++) {
      if (!completedById[sections[i].id]) return i;
    }
    return sections.length;
  }, [sections, completedById]);

  const selected = useMemo(
    () => sections.find((s) => s.id === selectedId) || null,
    [sections, selectedId],
  );

  const validationActive = Boolean(
    selected && sections[firstIncompleteIndex]?.id === selected.id,
  );

  const completeCurrentStep = useCallback(
    /**
     * `extra` permet à un step spécifique de joindre des données métier au
     * payload de validation (ex : `structureChoice` pour la card "structure").
     * Le backend les ignore poliment si la card n'est pas du bon type, ce qui
     * garde cette signature uniforme pour tous les types de cards.
     */
    async (sectionId, extra = {}) => {
      try {
        setStepPersistError(null);
        await axios.post(
          API_VALIDATE_STEP,
          { sectionId, ...extra },
          { withCredentials: true },
        );
      } catch (e) {
        const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Unable to save step.';
        setStepPersistError(msg);
        return;
      }
      setCompletedById((p) => {
        let fi = -1;
        for (let i = 0; i < sections.length; i++) {
          if (!p[sections[i].id]) {
            fi = i;
            break;
          }
        }
        if (fi < 0 || sections[fi].id !== sectionId) return p;
        return { ...p, [sectionId]: true };
      });
    },
    [sections],
  );

  const showContinueFooter = useMemo(() => {
    if (listLoading || listError) return false;
    if (sections.length === 0) return true;
    return Boolean(completedById[sections[sections.length - 1].id]);
  }, [listLoading, listError, sections, completedById]);

  const handleContinue = useCallback(() => {
    const canLeave =
      sections.length === 0 ||
      (sections.length > 0 && completedById[sections[sections.length - 1].id]);
    if (!canLeave) return;
    navigate('/members/onboarding/ending');
  }, [sections, completedById, navigate]);

  const isStepSelectable = (index) => index <= firstIncompleteIndex;

  const completedStepCount = useMemo(
    () => sections.reduce((n, s) => (completedById[s.id] ? n + 1 : n), 0),
    [sections, completedById],
  );

  return (
    <div className="private-pages members-onboarding">
      <div className="user-admin-container members-onboarding__shell">
        <header className="members-onboarding__header">
          <h1 className="members-onboarding__title">Onboarding</h1>
          <p className="members-onboarding__subtitle">
            Complete each step in order. When a step is validated, the next one unlocks — open it from
            the list when you are ready (navigation does not switch automatically).
          </p>
        </header>

        {listLoading ? (
          <p className="members-onboarding__load-hint">Loading steps…</p>
        ) : null}

        {!listLoading && listError ? (
          <p className="members-onboarding__error" role="alert">
            {listError}
          </p>
        ) : null}

        {!listLoading && stepPersistError ? (
          <p className="members-onboarding__error" role="alert">
            {stepPersistError}
          </p>
        ) : null}

        {!listLoading && !listError && sections.length === 0 ? (
          <p className="members-onboarding-steps__empty">
            No onboarding steps are available yet. You can continue to the final screen.
          </p>
        ) : null}

        {!listLoading && !listError && sections.length > 0 ? (
          <div className="members-onboarding__steps-layout">
            <nav className="members-onboarding__steps-nav" aria-label="Onboarding steps">
              <div className="members-onboarding__steps-nav-panel">
                <ul className="members-onboarding__steps-nav-list">
                  {sections.map((s, index) => {
                  const isActive = s.id === selectedId;
                  const done = Boolean(completedById[s.id]);
                  const selectable = isStepSelectable(index);
                  const isNext = index === firstIncompleteIndex && !done;
                  const navBadge = navBadgeById[s.id];
                  const payload =
                    s.payload && typeof s.payload === 'object' && !Array.isArray(s.payload)
                      ? s.payload
                      : {};
                  const remFrac = navTimerRemainingFraction(navBadge, payload);
                  const title = s.title?.trim() || 'Untitled';
                  return (
                    <li key={s.id} className="members-onboarding__steps-nav-item">
                      <button
                        type="button"
                        className={[
                          'members-onboarding__steps-nav-btn',
                          isActive ? 'members-onboarding__steps-nav-btn--active' : '',
                          done ? 'members-onboarding__steps-nav-btn--done' : '',
                          isNext ? 'members-onboarding__steps-nav-btn--next' : '',
                          !selectable ? 'members-onboarding__steps-nav-btn--locked' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        disabled={!selectable}
                        aria-current={isActive ? 'step' : undefined}
                        onClick={() => {
                          if (selectable) setSelectedId(s.id);
                        }}
                      >
                        <span
                          className={[
                            'members-onboarding__steps-nav-btn-disc',
                            isActive ? 'members-onboarding__steps-nav-btn-disc--active' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          aria-hidden
                        >
                          <MembersOnboardingStepDisc
                            stepIndex={index}
                            isActive={isActive}
                            locked={!selectable}
                            remainingFraction={remFrac}
                          />
                        </span>
                        <span className="members-onboarding__steps-nav-label">{title}</span>
                        <span className="members-onboarding__steps-nav-trailing">
                          {done ? (
                            <span className="members-onboarding__steps-nav-check" aria-hidden>
                              ✓
                            </span>
                          ) : navBadge ? (
                            <>
                              <MembersOnboardingNavClockIcon />
                              <span className="members-onboarding__steps-nav-badge">
                                {formatNavClockDisplay(navBadge) || navBadge}
                              </span>
                            </>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  );
                  })}
                </ul>
                <div className="members-onboarding__steps-progressCard">
                  <p className="members-onboarding__steps-progressCard-eyebrow">Onboarding progress</p>
                  <p className="members-onboarding__steps-progressCard-count">
                    {completedStepCount} / {sections.length} completed
                  </p>
                  <progress
                    className="members-onboarding__steps-progress-meter"
                    value={completedStepCount}
                    max={sections.length || 1}
                  />
                </div>
              </div>
            </nav>

            <div
              className={[
                'members-onboarding__steps-main',
                selected?.type === 'structure' ? 'members-onboarding__steps-main--structureDock' : '',
                structureBgHlsUrl && selected?.type === 'structure'
                  ? 'members-onboarding__steps-main--videoBg'
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {structureBgHlsUrl && selected?.type === 'structure' ? (
                <MembersOnboardingStepsMainBgVideo src={structureBgHlsUrl} />
              ) : null}
              {selected ? (
                <div className="members-onboarding__steps-main-body">
                  <MembersOnboardingStepPanel
                    section={selected}
                    validationActive={validationActive}
                    sectionCompleted={Boolean(completedById[selected.id])}
                    masterTags={masterTags}
                    onStepComplete={completeCurrentStep}
                    onNavBadge={setNavBadge}
                    onStructureBackgroundVideoChange={handleStructureBackgroundVideoChange}
                  />
                </div>
              ) : (
                <p className="members-onboarding-steps__empty">Select a step.</p>
              )}
            </div>
          </div>
        ) : null}

        {showContinueFooter ? (
          <div className="members-onboarding__footer">
            <button type="button" className="members-onboarding__continue" onClick={handleContinue}>
              Continue
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
